import React, { useState, useCallback, useEffect } from "react";
import { Col, Container, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DhlBranchFilters from "../components/DhlBranch/DhlBranchFilters";
import DhlBranchTable from "../components/DhlBranch/DhlBranchTable";
import PaginationComponent from "../components/PaginationComponent";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import searchIcon from "../assets/images/search_rgb_red.svg";
import "../assets/styles/dhlBranch.scss";
import "../assets/styles/addresses.scss";
import api from "../services/httpClient";
import usePersistedFilters from "../hooks/usePersistedFilters";
import usePersistedPageSize from "../hooks/usePersistedPageSize";

const DhlBranchList = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize("dhlBranches", 20);
  const { filters, handleFilterChange, clearFilters, hasActiveFilters } = usePersistedFilters("dhlBranches");
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sortConfig, setSortConfig] = useState({ key: "Ex", direction: "asc" });

  const [data, setData] = useState({
    rows: [],
    total: 0,
    totalPages: null,
  });

  const fetchData = useCallback(
    async (
      pageNumber = currentPage,
      pageSize = itemsPerPage,
      filtersToApply = appliedFilters,
      sortConfigToApply = sortConfig
    ) => {
      try {
        const offset = (pageNumber - 1) * pageSize;

        const requestBody = {
          sort: sortConfigToApply.key,
          order: sortConfigToApply.direction,
          offset: offset,
          limit: pageSize,
          search: {
            ex: filtersToApply.ex || "",
            dr: filtersToApply.dr || "",
            postcodesText: filtersToApply.postcodesText || "",
          },
        };

        const response = await api.post(`/Branch/List`, requestBody);

        const result = response.data?.data || response.data;
        const rows = Array.isArray(result.items) ? result.items : (Array.isArray(result.rows) ? result.rows : []);
        const total = result.itemsCount ?? result.total ?? 0;
        const totalPages = result.totalPages ?? null;
        setData({ rows, total, totalPages });
      } catch (error) {
        console.error("Error fetching branch list:", error);
        setData({ rows: [], total: 0 });
      }
    },
    [currentPage, itemsPerPage, appliedFilters, sortConfig]
  );

  useEffect(() => {
    fetchData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchData(1, itemsPerPage, filters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    fetchData(currentPage, itemsPerPage, appliedFilters, newSortConfig);
  };

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <div className="dhl-branch-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="Branch Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Oddziały DHL
            </h3>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={(props) => <Tooltip id="search-tooltip" {...props}>Szukaj</Tooltip>}
            >
              <Button className="addresses-button" onClick={handleSearch}>
                <img
                  src={searchIcon}
                  alt="Search"
                  style={{ width: "20px", height: "20px" }}
                />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={(props) => <Tooltip id="add-tooltip" {...props}>Dodaj oddział</Tooltip>}
            >
              <Button
                className="addresses-button"
                variant="outline-success"
                onClick={() => navigate("/administration/dhl-branches/add")}
              >
                +
              </Button>
            </OverlayTrigger>
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <DhlBranchFilters onFilterChange={handleFilterChange} onSearch={handleSearch} filters={filters} />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row className="justify-content-end mb-3">
          <Col xs="auto">
            <Button
              variant={hasActiveFilters ? "success" : "outline-secondary"}
              size="sm"
              className="me-3"
              onClick={() => { clearFilters(); setAppliedFilters({}); fetchData(1, itemsPerPage, {}); setCurrentPage(1); }}
            >
              Wyczyść filtry
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <DhlBranchTable
              data={data.rows}
              onSortChange={handleSortChange}
              onRefresh={handleRefresh}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={data.rows.length}
            totalCount={data.total}
            totalPages={data.totalPages}
            pageNumber={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchData(page, itemsPerPage);
            }}
            onItemsPerPageChange={(pageSize) => {
              const size = Number(pageSize);
              setItemsPerPage(size);
              setCurrentPage(1);
              fetchData(1, size);
            }}
            itemsPerPageOptions={[20, 50, 100]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default DhlBranchList;
